import { useMemo, useState } from "react";
import type { PermissionItem } from "@/types";

interface PermissionCoverageTableProps {
  permissions: PermissionItem[];
  permissionsLoading: boolean;
  modules: any[];
  updatePermission: (payload: {
    id: string;
    moduleCode: string;
  }) => Promise<any>;
  isUpdating: boolean;
}

export function PermissionCoverageTable({
  permissions,
  permissionsLoading,
  modules,
  updatePermission,
  isUpdating,
}: PermissionCoverageTableProps) {
  const [permissionSearch, setPermissionSearch] = useState("");
  const [showMissingOnly, setShowMissingOnly] = useState(true);

  const permissionRows = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    return (permissions ?? []).filter((perm) => {
      if (showMissingOnly && perm.moduleCode && perm.moduleMappingConsistent) {
        return false;
      }
      if (!term) return true;
      return (
        perm.key.toLowerCase().includes(term) ||
        perm.group.toLowerCase().includes(term) ||
        perm.description?.toLowerCase().includes(term)
      );
    });
  }, [permissions, permissionSearch, showMissingOnly]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Permission coverage
          </h3>
          <p className="text-sm text-slate-500">
            Assign module codes to permission keys so the app can gate access
            consistently.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={permissionSearch}
            onChange={(e) => setPermissionSearch(e.target.value)}
            placeholder="Search permissions"
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showMissingOnly}
              onChange={(e) => setShowMissingOnly(e.target.checked)}
            />
            Missing only
          </label>
        </div>
      </div>
      <div className="overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Group</th>
              <th className="px-4 py-3">Permission</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Module</th>
            </tr>
          </thead>
          <tbody>
            {permissionsLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={4}>
                  Loading permissions...
                </td>
              </tr>
            ) : permissionRows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={4}>
                  No permissions match the current filter.
                </td>
              </tr>
            ) : (
              permissionRows.map((perm: PermissionItem) => (
                <tr
                  key={perm.id}
                  className="border-b border-slate-100 last:border-none"
                >
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {perm.group}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {perm.key}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {perm.description || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <select
                        value={perm.moduleCode ?? ""}
                        onChange={(e) =>
                          updatePermission({
                            id: perm.id,
                            moduleCode: e.target.value,
                          })
                        }
                        disabled={isUpdating}
                        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
                      >
                        <option value="">Unassigned</option>
                        {modules.map((m) => (
                          <option key={m.id} value={m.code}>
                            {m.code}
                          </option>
                        ))}
                      </select>
                      {perm.moduleCode && !perm.moduleMappingConsistent && (
                        <button
                          type="button"
                          disabled={isUpdating}
                          onClick={() =>
                            updatePermission({
                              id: perm.id,
                              moduleCode: perm.moduleCode!,
                            })
                          }
                          title={`Runtime mappings: ${
                            perm.mappedModuleCodes.join(", ") || "none"
                          }`}
                          className="h-8 rounded-lg bg-amber-100 px-2 text-[10px] font-semibold uppercase tracking-wide text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                        >
                          Repair
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
